import React from 'react';
import { BarData } from '@/types';
import { cn } from '@/lib/utils';

interface SimpleChartProps {
  data: BarData[];
  symbol: string;
  className?: string;
  height?: number;
}

export const SimpleChart: React.FC<SimpleChartProps> = ({
  data,
  symbol,
  className,
  height = 400
}) => {
  // Calculate basic statistics
  const hasData = data && data.length > 0;
  const latestBar = hasData ? data[data.length - 1] : null;
  const latestPrice = latestBar ? (latestBar.closeBidPrice + latestBar.closeAskPrice) / 2 : 0;

  // Simple price range calculation
  const prices = data.map(bar => (bar.closeBidPrice + bar.closeAskPrice) / 2);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;

  return (
    <div 
      className={cn("w-full bg-background border rounded-lg", className)}
      style={{ height }}
    >
      {hasData ? (
        <div className="h-full flex flex-col">
          {/* Simple Chart Visualization */}
          <div className="flex-1 bg-background relative overflow-hidden">
            <div className="absolute inset-0">
              {/* Price Line Chart */}
              <svg className="w-full h-full" viewBox="0 0 100 50" style={{ background: 'hsl(var(--background))' }}>
                {/* Grid lines */}
                <defs>
                  <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="hsl(var(--border))" strokeWidth="0.1"/>
                  </pattern>
                </defs>
                <rect width="100" height="50" fill="url(#grid)" />
                
                {/* Candlestick bars */}
                {data.map((bar, index) => {
                  const open = (bar.openBidPrice + bar.openAskPrice) / 2;
                  const close = (bar.closeBidPrice + bar.closeAskPrice) / 2;
                  const high = (bar.highBidPrice + bar.highAskPrice) / 2;
                  const low = (bar.lowBidPrice + bar.lowAskPrice) / 2;
                  const isGreen = close >= open;
                  
                  const x = (index / Math.max(data.length - 1, 1)) * 100;
                  const openY = ((maxPrice - open) / (priceRange || 1)) * 50;
                  const closeY = ((maxPrice - close) / (priceRange || 1)) * 50;
                  const highY = ((maxPrice - high) / (priceRange || 1)) * 50;
                  const lowY = ((maxPrice - low) / (priceRange || 1)) * 50;
                  
                  return (
                    <g key={index}>
                      {/* High-low line */}
                      <line
                        x1={x}
                        y1={highY}
                        x2={x}
                        y2={lowY}
                        stroke={isGreen ? "#26a69a" : "#ef5350"}
                        strokeWidth="0.2"
                      />
                      {/* Body rectangle */}
                      <rect
                        x={x - 0.3}
                        y={Math.min(openY, closeY)}
                        width="0.6"
                        height={Math.abs(closeY - openY) || 0.1}
                        fill={isGreen ? "#26a69a" : "#ef5350"}
                      />
                    </g>
                  );
                })}
              </svg>
              
              {/* Price labels */}
              <div className="absolute top-2 right-2 text-xs text-muted-foreground bg-background px-1 rounded">
                ${maxPrice.toFixed(2)}
              </div>
              <div className="absolute bottom-2 right-2 text-xs text-muted-foreground bg-background px-1 rounded">
                ${minPrice.toFixed(2)}
              </div>
              
              {/* Current price line */}
              <div 
                className="absolute left-0 right-0 border-t border-dashed border-foreground/30"
                style={{ 
                  top: `${((maxPrice - latestPrice) / (priceRange || 1)) * 100}%` 
                }}
              >
                <div className="absolute right-2 -top-2 text-xs text-foreground bg-background px-1 rounded font-medium">
                  ${latestPrice.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-full flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="text-6xl mb-4">📈</div>
            <div className="text-muted-foreground">Loading chart data...</div>
            <div className="text-sm text-muted-foreground mt-2">
              {symbol} data incoming...
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleChart;